import { useCallback } from "react";
import { JDCreateRequest, JDStatus, JobType } from "../../../redux/features/jd-slice";
import Button from "../../../components/button";
import DOMPurify from "dompurify";
import { format } from "date-fns";

interface JobPreviewModalProps {
  open: boolean;
  onClose: () => void;
  job: JDCreateRequest;
}

export default function JobPreviewModal({ open, onClose, job }: JobPreviewModalProps) {
  const createMarkup = useCallback((html: string) => {
    return {
      __html: DOMPurify.sanitize(html),
    };
  }, []);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center  backdrop-blur-3xl">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-5xl max-h-[95vh] overflow-y-auto">
        <div className="p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Job Description Preview</h2>
          <article className="space-y-6">
            {/* Title */}
            <h1 className="text-3xl font-bold text-gray-800">{job.title || "Untitled Job"}</h1>

            {/* Job Type, Location, Deadline */}
            <section className="flex flex-col">
              <div>
                <p className="text-black">
                  {job.jobType
                    ? job.jobType
                        .split("_")
                        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(" ")
                    : "Not specified"}
                </p>
              </div>
              <div>
                <p className="text-black">
                  <span className="font-medium">Location:</span>{" "}
                  {job.location || "Not specified"}
                </p>
              </div>
              <div>
                <p className="text-black0">
                  <span className="font-medium">Deadline:</span>{" "}
                  {job.deadline ? format(new Date(job.deadline), "PP") : "Not specified"}
                </p>
              </div>
            </section>

            {/* Description */}
            <section className="space-y-2">
              <h2 className="text-xl font-semibold text-black">Description</h2>
              {job.description ? (
                <div
                  className="ql-editor ql-snow text-gray-600 space-y-3"
                  dangerouslySetInnerHTML={createMarkup(job.description)}
                />
              ) : (
                <p className="text-gray-600">No description provided.</p>
              )}
            </section>

            {/* Requirements */}
            <section className="space-y-2">
              <h2 className="text-xl font-semibold text-black">Requirements</h2>
              {job.requirements ? (
                <div
                  className="ql-editor ql-snow text-gray-600 space-y-3"
                  dangerouslySetInnerHTML={createMarkup(job.requirements)}
                />
              ) : (
                <p className="text-gray-600">No requirements provided.</p>
              )}
            </section>
          </article>

          {/* Close Button */}
          <div className="mt-6 flex justify-end">
            <Button
              label="Close"
              type="button"
              onClick={onClose}
              exClassName="bg-black text-white py-2 px-4 rounded-md hover:bg-gray-700 transition-colors"
            />
          </div>
        </div>
      </div>
    </div>
  );
}